import React from "react";
import _ from "lodash";
import clsx from "clsx";

import Grid from "@material-ui/core/Grid";
import Chip from "@material-ui/core/Chip";

import BaseDialog from "../../../../../../../components/BaseDialog/BaseDialog";
import PrimaryButton from "../../../../../../../components/Buttons/PrimaryButton";
import SelectFormElementV2
  from "../../../../../../RiskProfiling/components/StepContent/components/formElement/SelectFormElementV2";
import {getModelPortfolioTransactionTypeOptions} from "../../../../../constants";
import BcaIconButton from "../../../../../../../components/Buttons/BcaIconButton/BcaIconButton";
import TruncatedText from "../../../../../../../components/TruncateText";

import useStyles from "./styles";


const FIELD_DEFINITIONS = [
  {
    key: 'depotType',
    label: 'Vertragsart',
    placeholder: 'Vertragsart auswählen',
    getOptions: (portfolio) => portfolio.availableDepotTypes || [],
    getValue: (portfolio) => _.get(portfolio, 'depotType.value', null),
    isVisible: () => true,
    required: true,
  },
  {
    key: 'modelPortfolioTransactionType',
    label: 'Anlagevariante',
    placeholder: 'Anlagevariante auswählen',
    getOptions: (portfolio) => getModelPortfolioTransactionTypeOptions(portfolio.tradingType),
    getValue: (portfolio) => portfolio.modelPortfolioTransactionType || null,
    isVisible: (portfolio) => portfolio.isModelportfolio,
    required: true,
  },
  {
    key: 'discount',
    label: 'Depotrabatt',
    getOptions: (portfolio) => portfolio.availableDiscounts || [],
    getValue: (portfolio) => portfolio.discount || null,
    isVisible: (portfolio) => _.get(portfolio, 'ordering_discounts_allowed', []).includes('depot'),
    getTooltip: (portfolio) => portfolio.combinedDiscounts && "Der ausgewählte Depotrabatt wird auf alle im depotbefindlichen Produkte angewendet. Der Depotrabatt bleibt dauerhaft auf Produktebene aktiv und gilt für alle zukünftigen Transaktionen, es sei denn, er wird geändert. Zusätzlich kann ein Einmalrabatt auf Produktebene vergeben werden. Der Depotrabatt und der Einmalrabatt werden addiert zum Gesamtrabatt auf Produktebene der jeweiligen Transaktion.",
    getBadgeLabel: (label) => `${label} Depotrabatt`,
    required: false,
  },
];

const getBadges = (portfolio, hasError) => {
  const badges = [];

  FIELD_DEFINITIONS.forEach(({key, label, getOptions, getValue, required, isVisible, getBadgeLabel}) => {
    const value = getValue(portfolio);
    if (!value) {
      if(hasError && required && isVisible(portfolio)){
        badges.push({key, label: `Keine ${label}`, className: 'error'})
      }

      return;
    }
    const badgeLabel = _.get(_.find(getOptions(portfolio), {value}), 'label');
    if (badgeLabel) badges.push({key, label: !!getBadgeLabel ? getBadgeLabel(badgeLabel) : badgeLabel});
  });

  return badges;
};

const buildFormData = (portfolio) => {
  const data = {discount: portfolio.discount || null};
  FIELD_DEFINITIONS.forEach(({key, getValue}) => {
    data[key] = getValue(portfolio);
  });
  return data;
};


const PortfolioConfig = ({portfolio, onSave, hasError}) => {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [formData, setFormData] = React.useState({});

  const badges = React.useMemo(
    () => getBadges(portfolio, hasError),
    [portfolio, hasError]);

  const visibleFields = React.useMemo(
    () => FIELD_DEFINITIONS.filter((field) => field.isVisible(portfolio)),
    [portfolio]
  );

  const handleOpen = (e) => {
    e.stopPropagation();
    setFormData(buildFormData(portfolio));
    setDialogOpen(true);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setDialogOpen(false);
  };

  const handleSave = async (e) => {
    e.stopPropagation();

    const currentData = buildFormData(portfolio);
    const changes = {};

    Object.keys(formData).forEach((key) => {
      if (formData[key] !== currentData[key]) {
        changes[key] = formData[key];
      }
    });

    if (!_.isEmpty(changes)) {
      await onSave(changes);
    }

    setDialogOpen(false);
  };

  const handleFieldChange = (key) => (value) => {
    setFormData((prev) => ({...prev, [key]: value}));
  };

  if (!portfolio) return null;

  return (
    <>
      <Grid container spacing={1} justify={"flex-end"} alignItems={"center"} wrap="nowrap" className={classes.badgeRow}>
        {badges.map(({key, label, className}) => (
          <Grid item key={key} className={classes.badgeItem}>
            <Chip label={<TruncatedText text={label} />} size="small" className={clsx(classes.chip, className)} />
          </Grid>
        ))}
        <Grid item className={classes.editButton}>
          <BcaIconButton icon={<i className={'far fa-edit'} />} onClick={handleOpen} />
        </Grid>
      </Grid>

      <BaseDialog
        open={dialogOpen}
        handleClose={handleClose}
        title="Konditionen einrichten"
        maxWidth="sm"
        actions={
          <Grid container justify={"flex-end"} spacing={2}>
            <Grid item xs={12} sm="auto">
              <PrimaryButton text="Abbrechen" variant="outlined" onButtonClick={handleClose} />
            </Grid>
            <Grid item xs={12} sm="auto">
              <PrimaryButton text="Speichern" onButtonClick={handleSave} />
            </Grid>
          </Grid>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div>Für Depot:</div>
            <div><b>{portfolio.data.name}</b></div>
          </Grid>

          {visibleFields.map((field) => (
            <Grid item xs={12} md={6} key={field.key}>
              <SelectFormElementV2
                label={field.label}
                value={formData[field.key]}
                options={field.getOptions(portfolio)}
                onChange={handleFieldChange(field.key)}
                placeholder={field.placeholder}
                required={field.required}
                tooltip={field.getTooltip ? field.getTooltip(portfolio) : undefined}
                newDesign
              />
            </Grid>
          ))}
        </Grid>
      </BaseDialog>
    </>
  );
};

export default PortfolioConfig;
